// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isBidItem } from "@steiner/App/Store/Selectors/CartsSelector";
import React, { FC } from "react";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: LinkPresentationProps;
    cartLineImage?: ProductImageStyles;
    cartLineNonStockImage?: LazyImageProps;
    contentWrapper?: InjectableCss;
    lineDescription?: ProductDescriptionStyles;
    nonStockDescription?: TypographyPresentationProps;
    removeCartLine?: IconPresentationProps;
    priceContainer?: InjectableCss;
    priceWrapper?: InjectableCss;
    priceTitle?: TypographyPresentationProps;
    priceValue?: TypographyPresentationProps;
    extPriceWrapper?: InjectableCss;
    extPriceTitle?: TypographyPresentationProps;
    extPriceValue?: TypographyPresentationProps;
    qtyAndWarningIconWrapper?: InjectableCss;
    alertIcon?: IconPresentationProps;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    qtyAndPriceWrapper?: InjectableCss;
    priceNotAvailable?: TypographyPresentationProps;
}

export const miniCartLineStyles: MiniCartLineStyles = {};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({ cartLine, cart, qtyOrderedChangeHandler, removeCartLineClickHandler }) => {
    const { showPricing } = useAppSelector(state => ({
        showPricing: state.context.showPricing,
    }));
    const isWidgetNonStockItem = cartLine.properties?.isWidgetNonStockItem === "true";

    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                {isWidgetNonStockItem ? (
                    <LazyImage
                        src={cartLine.smallImagePath}
                        altText={`${translate("Image of ")}: ${cartLine.properties?.nonStockDescription}`}
                        {...styles.cartLineNonStockImage}
                    />
                ) : (
                    <ProductImage product={cartLine} extendedStyles={styles.cartLineImage} />
                )}

                <StyledWrapper {...styles.contentWrapper}>
                    {isWidgetNonStockItem ? (
                        <Typography {...styles.nonStockDescription}>
                            {cartLine.properties?.nonStockDescription}
                        </Typography>
                    ) : (
                        <ProductDescription product={cartLine} extendedStyles={styles.lineDescription} />
                    )}
                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                        <StyledWrapper {...styles.qtyAndWarningIconWrapper}>
                            <CartLineQuantity
                                cart={cart as Cart}
                                editable={!isBidItem(cartLine) && !cartLine.isPromotionItem && !cartLine.properties.cut}
                                onQtyOrderedChange={(inputQty: number) => qtyOrderedChangeHandler(cartLine, inputQty)}
                                extendedStyles={styles.cartLineQuantityStyles}
                            />
                            {isWidgetNonStockItem && <Icon {...styles.alertIcon} />}
                        </StyledWrapper>
                        {!cartLine.quoteRequired && !cart?.cartNotPriced && cartLine.pricing && showPricing && (
                            <StyledWrapper
                                {...styles.priceContainer}
                                data-test-selector="cartline_extendedUnitNetPrice"
                            >
                                {(isWidgetNonStockItem || !cartLine.pricing?.actualPrice) && !isBidItem(cartLine) ? (
                                    <Typography {...styles.priceNotAvailable}>
                                        {siteMessage("STR_Price_Not_Available")}
                                    </Typography>
                                ) : (
                                    <>
                                        <StyledWrapper {...styles.priceWrapper}>
                                            <Typography {...styles.priceTitle}>{translate("Price")}:</Typography>
                                            <Typography {...styles.priceValue}>
                                                {cartLine.pricing?.actualPriceDisplay}
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.priceWrapper}>
                                            <Typography {...styles.extPriceTitle}>
                                                {translate("Ext. Price")}:
                                            </Typography>
                                            <Typography {...styles.extPriceValue}>
                                                {cartLine.pricing?.extendedActualPriceDisplay}
                                            </Typography>
                                        </StyledWrapper>
                                    </>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>

                {!cartLine.isPromotionItem && cartLine.properties["isBidItem"] !== "true" && (
                    <Icon {...styles.removeCartLine} onClick={() => removeCartLineClickHandler(cartLine)} />
                )}
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
